import { Stats } from 'wb-spotify/graphql/generated';

export type SpotifyMetric = keyof Omit<Stats, '__typename'>;

export const SPOTIFY_SUPERGRID_STATISTICS_METRICS_FIELDS: SpotifyMetric[] = [
	'adCompletes',
	'clicks',
	'listeners',
	'newListeners',
	'playedTo25',
	'playedTo50',
	'playedTo75',
	'playedTo100',
	'starts',
	'streams',
	'unmutes',
	'videoViews',
	'videoExpands',
	'skadAppInstalls',
	'kochavaAppInstalls',
	'appsflyerAppInstalls',
	'modeledAddToCart',
	'modeledLeads',
	'modeledPageViews',
	'modeledPurchases',
	'unmodeledAddToCart',
	'unmodeledLeads',
	'unmodeledPageViews',
	'unmodeledPurchases',
	'completionRate',
	'ctr',
	'intentRate',
	'listenerConversionRate',
	'newListenerConversionRate',
	'videoExpandRate',
	'frequency',
	'frequencyOfAdCompletes',
	'reach',
	'reachOfAdCompletes',
	'spend',
	'cpm',
	'cpcl',
	'avgStreamsPerListener',
	'avgStreamsPerNewListener',
];

export const SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS: SpotifyMetric[] = [
	// 'date',
	'dayOfMonth',
	'weekdayNumber',
	'weekdayName',
	'weekNumber',
	'monthNumber',
	'monthName',
	'quarter',
	'year',
	'yearMonth',
	'country',
	'platform',
	'gender',
	'age',
	'genre',
];

export const SPOTIFY_SUPERGRID_STATISTICS_DATE_DIMENSIONS: SpotifyMetric[] = [
	'date',
	'dayOfMonth',
	'weekdayNumber',
	'weekdayName',
	'weekNumber',
	'monthNumber',
	'monthName',
	'quarter',
	'year',
	'yearMonth',
];

export const SPOTIFY_SUPERGRID_STATISTICS_METRICS_WITHOUT_DIMENSIONS: SpotifyMetric[] = SPOTIFY_SUPERGRID_STATISTICS_METRICS_FIELDS
	.filter(metric => !SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS.includes(metric) && metric !== 'date');

export const SPOTIFY_SUPERGRID_STATISTICS_LIFETIME_METRICS = [
	'clicks',
	'conversionRate',
	'ctr',
	'eCpm',
	'impressions',
	'intentRate',
	'listeners',
	'newListeners',
	'newListenerConversionRate',
	'newListenerStreams',
	'paidListens',
	'paidListensReach',
	'reach',
	'streams',
	'streamsPerNewListener',
	'streamsPerUser',
	'videoViews',
	'videoExpands',
	'videoExpandRate',
];

export const SPOTIFY_SUPERGRID_STATISTICS_METRICS_CATEGORIZED = [
	{
		value:   'general',
		label:   'General performance',
		metrics: [
			{ value: 'listeners', label: 'Listeners' },
			{ value: 'newListeners', label: 'New Listeners' },
			{ value: 'starts', label: 'Starts' },
			{ value: 'streams', label: 'Streams' },
			{ value: 'reach', label: 'Reach' },
			{ value: 'frequency', label: 'Frequency' },
		],
	},
	{
		value:   'engagement',
		label:   'Engagement',
		metrics: [
			{ value: 'playedTo25', label: 'Played To 25%' },
			{ value: 'playedTo50', label: 'Played To 50%' },
			{ value: 'playedTo75', label: 'Played To 75%' },
			{ value: 'playedTo100', label: 'Played To 100%' },
			{ value: 'clicks', label: 'Clicks' },
			{ value: 'unmutes', label: 'Unmutes' },
			{ value: 'adCompletes', label: 'Ad Completes' },
		],
	},
	{
		value:   'video',
		label:   'Video Metrics',
		metrics: [
			{ value: 'videoViews', label: 'Video Views' },
			{ value: 'videoExpands', label: 'Video Expands' },
			{ value: 'videoExpandRate', label: 'Video Expand Rate' },
		],
	},
	{
		value:   'conversions',
		label:   'Conversions',
		metrics: [
			{ value: 'skadAppInstalls', label: 'SKAD App Installs' },
			{ value: 'kochavaAppInstalls', label: 'Kochava App Installs' },
			{ value: 'appsflyerAppInstalls', label: 'AppsFlyer App Installs' },
			{ value: 'modeledAddToCart', label: 'Modeled Add To Cart' },
			{ value: 'modeledLeads', label: 'Modeled Leads' },
			{ value: 'modeledPageViews', label: 'Modeled Page Views' },
			{ value: 'modeledPurchases', label: 'Modeled Purchases' },
			{ value: 'unmodeledAddToCart', label: 'Unmodeled Add To Cart' },
			{ value: 'unmodeledLeads', label: 'Unmodeled Leads' },
			{ value: 'unmodeledPageViews', label: 'Unmodeled Page Views' },
			{ value: 'unmodeledPurchases', label: 'Unmodeled Purchases' },
		],
	},
	{
		value:   'rates',
		label:   'Rates & Ratios',
		metrics: [
			{ value: 'completionRate', label: 'Completion Rate' },
			{ value: 'ctr', label: 'CTR' },
			{ value: 'intentRate', label: 'Intent Rate' },
			{ value: 'listenerConversionRate', label: 'Listener Conversion Rate' },
			{ value: 'newListenerConversionRate', label: 'New Listener Conversion Rate' },
			{ value: 'avgStreamsPerListener', label: 'Avg Streams Per Listener' },
			{ value: 'avgStreamsPerNewListener', label: 'Avg Streams Per New Listener' },
		],
	},
	{
		value:   'monetization',
		label:   'Monetization',
		metrics: [
			{ value: 'spend', label: 'Spend' },
			{ value: 'cpm', label: 'CPM' },
			{ value: 'cpcl', label: 'CPCL' },
			{ value: 'reachOfAdCompletes', label: 'Reach Of Ad Completes' },
			{ value: 'frequencyOfAdCompletes', label: 'Frequency Of Ad Completes' },
		],
	},
];

export const SPOTIFY_SUPERGRID_STATISTICS_METRICS_LIFETIME_CATEGORIZED = [
	{
		value:   'general',
		label:   'General performance',
		metrics: [
			{ value: 'listeners', label: 'Listeners' },
			{ value: 'newListeners', label: 'New Listeners' },
			{ value: 'streams', label: 'Streams' },
			{ value: 'reach', label: 'Reach' },
		],
	},
	{
		value:   'engagement',
		label:   'Engagement',
		metrics: [
			{ value: 'clicks', label: 'Clicks' },
			{ value: 'impressions', label: 'Impressions' },
		],
	},
	{
		value:   'video',
		label:   'Video Metrics',
		metrics: [
			{ value: 'videoViews', label: 'Video Views' },
			{ value: 'videoExpands', label: 'Video Expands' },
			{ value: 'videoExpandRate', label: 'Video Expand Rate' },
		],
	},
	{
		value:   'conversions',
		label:   'Conversions',
		metrics: [
			{ value: 'paidListens', label: 'Paid Listens' },
			{ value: 'paidListensReach', label: 'Paid Listens Reach' },
		],
	},
	{
		value:   'rates',
		label:   'Rates & Ratios',
		metrics: [
			{ value: 'conversionRate', label: 'Conversion Rate' },
			{ value: 'ctr', label: 'CTR' },
			{ value: 'intentRate', label: 'Intent Rate' },
			{ value: 'newListenerConversionRate', label: 'New Listener Conversion Rate' },
			{ value: 'streamsPerNewListener', label: 'Streams Per New Listener' },
			{ value: 'streamsPerUser', label: 'Streams Per User' },
			{ value: 'newListenerStreams', label: 'New Listener Streams' },
		],
	},
	{
		value:   'monetization',
		label:   'Monetization',
		metrics: [
			{ value: 'eCpm', label: 'ECPM' },
		],
	},
];

// TODO this is metrics which is allowed to lifetime report
export const LIFE_TIME_METRICS = [
	'impressions',
	'paidListens',
	'paidListensReach',
	'conversionRate',
	'streamsPerNewListener',
	'streamsPerUser',
	'newListenerStreams',
	'eCpm',
];
